/**
 * 2FA 验证页 — 用临时 token + TOTP code（或备份码）换 access+refresh
 *
 * 皮肤 1:1 移植 design/Novel-admin/Login.html step-2（6 格 OTP + 用户 pill + 备份码切换）；
 * 逻辑保持不变：verify2FA(token, {code|backup_code}) → setSession → 跳 from。
 */

import { type FormEvent, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { AdminApiError } from '~/api/errors';
import { applyLoginDefaults } from '~/app/app-scope-store';
import { verify2FA } from '~/hooks/use-auth';
import { useAuthStore } from '~/stores/auth-store';
import { toast } from '~/ui/Toast';

export default function Verify2FAPage() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const fromPath = params.get('from') ?? '/';
  const navigate = useNavigate();
  // 登录第一步经 router state 传来的邮箱 + 「14 天保持登录」勾选（刷新会丢，按缺省兜底）
  const location = useLocation();
  const navState = location.state as { email?: string; remember?: boolean } | null;
  const email = navState?.email ?? '';
  // 「14 天保持登录」勾选经 URL query 透传（router state 整页刷新会丢、query 不丢）；
  // 缺省回退 router state、再回退登录页默认勾选(true)，避免刷新后误降级为 7 天 refresh
  const rememberParam = params.get('remember');
  const remember = rememberParam != null ? rememberParam === '1' : (navState?.remember ?? true);
  const displayName = email ? email.split('@')[0] : '运营账户';
  const avatarInitial = (email[0] ?? 'N').toUpperCase();
  // 返回登录第一步（保留 from 目标）—— 对齐设计稿 step-2 用户卡片右侧的「返回」入口
  const goBackToLogin = () => {
    const q = fromPath !== '/' ? `?from=${encodeURIComponent(fromPath)}` : '';
    navigate(`/auth/login${q}`);
  };
  const setSession = useAuthStore((s) => s.setSession);
  const [submitting, setSubmitting] = useState(false);
  // mode: 'totp' = 6 格验证码；'backup' = 8 位备份码
  const [mode, setMode] = useState<'totp' | 'backup'>('totp');
  const [digits, setDigits] = useState<string[]>(['', '', '', '', '', '']);
  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [backupCode, setBackupCode] = useState('');
  // 「在此设备记住 30 天 · 期间不再要求验证码」默认勾选（对齐设计稿 step-2）
  const [trustDevice, setTrustDevice] = useState(true);
  const inputsRef = useRef<(HTMLInputElement | null)[]>([]);
  // TOTP 30s 窗口剩余秒数（对齐设计稿「X 秒后刷新」提示，纯展示）
  const [ttl, setTtl] = useState(() => 30 - (Math.floor(Date.now() / 1000) % 30));
  useEffect(() => {
    const id = setInterval(() => setTtl(30 - (Math.floor(Date.now() / 1000) % 30)), 1000);
    return () => clearInterval(id);
  }, []);

  const submit = async (payload: { code?: string; backup_code?: string }) => {
    if (!token) {
      toast('临时 token 缺失，请重新登录', { tone: 'neg' });
      return;
    }
    setSubmitting(true);
    try {
      const res = await verify2FA(token, payload, { remember, trustDevice });
      setSession({ access: res.access, refresh: res.refresh, user: res.user });
      // 套用本机默认 App scope；from 无深链时跳默认落地页
      const defaultPage = applyLoginDefaults();
      navigate(fromPath !== '/' ? fromPath : defaultPage, { replace: true });
    } catch (err) {
      const message =
        err instanceof AdminApiError
          ? err.message
          : mode === 'backup'
            ? '备用码错误，请重试'
            : '验证码错误，请重试';
      setError(true);
      setErrorMessage(message);
      setTimeout(() => {
        setError(false);
        setErrorMessage('');
        setDigits(['', '', '', '', '', '']);
        inputsRef.current[0]?.focus();
      }, 1200);
      toast(message, { tone: 'neg' });
    } finally {
      setSubmitting(false);
    }
  };

  /** 写入第 i 格并自动跳焦；6 格填满自动提交 */
  const setDigit = (i: number, raw: string) => {
    const v = raw.replace(/[^0-9]/g, '').slice(-1);
    const next = [...digits];
    next[i] = v;
    setDigits(next);
    setError(false);
    setErrorMessage('');
    if (v && i < 5) inputsRef.current[i + 1]?.focus();
    if (next.every((d) => d) && !submitting) {
      void submit({ code: next.join('') });
    }
  };

  const onKeyDown = (i: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !digits[i] && i > 0) inputsRef.current[i - 1]?.focus();
    else if (e.key === 'ArrowLeft' && i > 0) inputsRef.current[i - 1]?.focus();
    else if (e.key === 'ArrowRight' && i < 5) inputsRef.current[i + 1]?.focus();
  };

  const onPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const text = e.clipboardData
      .getData('text')
      .replace(/[^0-9]/g, '')
      .slice(0, 6);
    if (!text) return;
    const next = ['', '', '', '', '', ''];
    text.split('').forEach((ch, j) => {
      next[j] = ch;
    });
    setDigits(next);
    inputsRef.current[Math.min(text.length, 5)]?.focus();
    if (text.length === 6 && !submitting) void submit({ code: text });
  };

  const onTotpSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const code = digits.join('');
    if (code.length === 6) void submit({ code });
  };

  const onBackupSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (/^\d{8}$/.test(backupCode)) void submit({ backup_code: backupCode });
  };

  const allFilled = digits.every((d) => d);

  return (
    <>
      <div className="step-indicator">
        <span>步骤 2 / 2</span>
        <span className="step-dot done" />
        <span className="step-dot active" />
      </div>

      <div className="step">
        <div style={{ marginBottom: 22 }}>
          <h2 className="form-title">{mode === 'totp' ? '双重验证' : '使用备用码'}</h2>
          <p className="form-sub">
            {mode === 'totp' ? (
              <>
                请输入{' '}
                <strong style={{ color: 'var(--fg-strong)', fontWeight: 500 }}>
                  Authenticator
                </strong>{' '}
                中显示的 6 位验证码
              </>
            ) : (
              <>
                请输入你的{' '}
                <strong style={{ color: 'var(--fg-strong)', fontWeight: 500 }}>8 位备用码</strong>
                ，每个备用码仅可使用一次
              </>
            )}
          </p>
        </div>

        {/* 用户卡片 — 当前登录账户 + Authenticator 标识 + 返回登录入口（对齐设计稿 step-2）*/}
        <div className="twofa-user" style={{ marginBottom: 18 }}>
          <div className="twofa-avatar">{avatarInitial}</div>
          <div className="twofa-info">
            <div className="twofa-name">{displayName}</div>
            {email ? <div className="twofa-mail">{email}</div> : null}
          </div>
          <div className="twofa-app-icon" title="Authenticator">
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.4"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="12" cy="12" r="9" />
              <path d="M12 7v5l3 2" />
            </svg>
          </div>
          <button
            type="button"
            className="btn-sso"
            style={{ padding: '0 10px', height: 28, fontSize: 11, flexShrink: 0 }}
            title="返回"
            onClick={goBackToLogin}
          >
            <svg
              width="11"
              height="11"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="m15 18-6-6 6-6" />
            </svg>
            返回
          </button>
        </div>

        {mode === 'totp' ? (
          <form
            onSubmit={onTotpSubmit}
            style={{ display: 'flex', flexDirection: 'column', gap: 16 }}
          >
            <div className="field">
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <label className="field-label" style={{ flex: 1 }}>
                  6 位验证码
                </label>
                <span
                  className="field-label"
                  style={{ textTransform: 'none', letterSpacing: 0, color: 'var(--fg-faint)' }}
                >
                  {ttl} 秒后刷新
                </span>
              </div>
              <div className="otp-row">
                {digits.map((d, i) => (
                  <input
                    key={i}
                    ref={(el) => {
                      inputsRef.current[i] = el;
                    }}
                    className={`otp-input${d ? ' filled' : ''}${error ? ' error' : ''}`}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    autoComplete="one-time-code"
                    value={d}
                    onChange={(e) => setDigit(i, e.target.value)}
                    onKeyDown={(e) => onKeyDown(i, e)}
                    onPaste={onPaste}
                    onFocus={(e) => e.target.select()}
                  />
                ))}
              </div>
            </div>

            {error && (
              <div className="err">
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="12" cy="12" r="9" />
                  <path d="M12 8v4" />
                  <path d="M12 16h.01" />
                </svg>
                <span>{errorMessage || '验证码错误，请重试'}</span>
              </div>
            )}

            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                fontSize: 12.5,
                color: 'var(--fg-muted)',
                cursor: 'pointer',
              }}
            >
              <input
                type="checkbox"
                checked={trustDevice}
                onChange={(e) => setTrustDevice(e.target.checked)}
                style={{ accentColor: 'var(--accent)', width: 15, height: 15, flexShrink: 0 }}
              />
              在此设备记住 30 天 · 期间不再要求验证码
            </label>

            <button
              type="submit"
              className="btn-big"
              style={{ marginTop: 4 }}
              disabled={!allFilled || submitting}
            >
              <span>{submitting ? '验证中…' : allFilled ? '验证并登录' : '输入 6 位验证码'}</span>
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M5 12h14" />
                <path d="m12 5 7 7-7 7" />
              </svg>
            </button>

            <div className="ga-hint">
              <svg
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{ color: 'var(--info)', flexShrink: 0 }}
              >
                <path d="M12 2 4 5v6c0 5 3.5 9.5 8 11 4.5-1.5 8-6 8-11V5z" />
              </svg>
              <span>
                在手机上打开 <span className="strong">Authenticator</span>，找到{' '}
                <span className="strong">Rivmoon Console{email ? `（${email}）` : ''}</span>
                ，验证码每 30 秒刷新。
              </span>
            </div>

            <div className="divider-row">无法访问验证器</div>

            <div style={{ display: 'flex', gap: 8 }}>
              <button
                type="button"
                className="btn-sso"
                style={{ fontSize: 12.5, flex: 1 }}
                onClick={() => {
                  setMode('backup');
                  setError(false);
                }}
              >
                <svg
                  width="13"
                  height="13"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.6"
                >
                  <path d="M18 3h-1a3 3 0 0 0-3 3v12a3 3 0 0 0 3 3h1a3 3 0 0 0 3-3 3 3 0 0 0-3-3H6a3 3 0 0 0-3 3 3 3 0 0 0 3 3h1a3 3 0 0 0 3-3V6a3 3 0 0 0-3-3H6a3 3 0 0 0-3 3 3 3 0 0 0 3 3h12a3 3 0 0 0 3-3 3 3 0 0 0-3-3" />
                </svg>
                使用备用码
              </button>
              {/* 验证器与备用码都不可用时的求助入口（对齐设计稿 step-2）*/}
              <a
                className="btn-sso"
                href="mailto:it-support@rivmoon.com"
                style={{ fontSize: 12.5, flex: 1, textDecoration: 'none' }}
              >
                <svg
                  width="13"
                  height="13"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <rect x="3" y="5" width="18" height="14" rx="2" />
                  <path d="m3 7 9 6 9-6" />
                </svg>
                联系管理员
              </a>
            </div>
          </form>
        ) : (
          <form
            onSubmit={onBackupSubmit}
            style={{ display: 'flex', flexDirection: 'column', gap: 16 }}
          >
            <div className="field">
              <label className="field-label">8 位备用码</label>
              <input
                className="input-big mono"
                style={{
                  fontFamily: "'Geist Mono', monospace",
                  fontSize: 16,
                  letterSpacing: '0.04em',
                  textAlign: 'center',
                }}
                inputMode="numeric"
                maxLength={8}
                placeholder="12345678"
                value={backupCode}
                onChange={(e) => {
                  setBackupCode(e.target.value.replace(/[^0-9]/g, '').slice(0, 8));
                  setError(false);
                  setErrorMessage('');
                }}
              />
            </div>
            {error && (
              <div className="err">
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="12" cy="12" r="9" />
                  <path d="M12 8v4" />
                  <path d="M12 16h.01" />
                </svg>
                <span>{errorMessage || '备用码错误，请重试'}</span>
              </div>
            )}
            <button
              type="submit"
              className="btn-big"
              disabled={!/^\d{8}$/.test(backupCode) || submitting}
            >
              {submitting ? '验证中…' : '验证备用码'}
            </button>
            <button
              type="button"
              className="btn-sso"
              style={{ fontSize: 12.5 }}
              onClick={() => {
                setMode('totp');
                setBackupCode('');
              }}
            >
              返回验证码登录
            </button>
          </form>
        )}
      </div>
    </>
  );
}
